import { cmsMediaUrl } from "../lib/cms";

/**
 * Renders any content block. Falls back gracefully when fields are empty.
 */
export const ContentBlockRenderer = ({ block }) => {
  if (!block || !block.is_visible) return null;
  switch (block.type) {
    case "hero":
      return <HeroBlock block={block} />;
    case "text":
      return <TextBlock block={block} />;
    case "image":
      return <ImageBlock block={block} />;
    case "text_image":
      return <TextImageBlock block={block} />;
    case "quote":
      return <QuoteBlock block={block} />;
    case "cta":
      return <CtaBlock block={block} />;
    case "gallery":
      return <GalleryBlock block={block} />;
    case "story_highlight":
    case "service_block":
    default:
      return <TextBlock block={block} />;
  }
};

const Wrap = ({ children, dataTestId }) => (
  <section
    data-testid={dataTestId}
    className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-16 md:py-24"
  >
    {children}
  </section>
);

const HeroBlock = ({ block }) => (
  <Wrap dataTestId={`block-hero-${block.id}`}>
    <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
      {block.subtitle}
    </p>
    <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white tracking-tight max-w-3xl leading-[1.05]">
      {block.title}
    </h2>
    {block.body && (
      <p className="mt-6 text-base text-zinc-300 font-body max-w-2xl leading-relaxed">
        {block.body}
      </p>
    )}
  </Wrap>
);

const TextBlock = ({ block }) => (
  <Wrap dataTestId={`block-text-${block.id}`}>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        {block.subtitle && (
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-4">
            {block.subtitle}
          </p>
        )}
        {block.title && (
          <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-[1.1]">
            {block.title}
          </h2>
        )}
      </div>
      <div className="md:col-span-7 md:col-start-6">
        {block.body && (
          <p className="text-base text-zinc-300 font-body leading-relaxed whitespace-pre-line">
            {block.body}
          </p>
        )}
        {block.button_text && block.button_link && (
          <a
            href={block.button_link}
            className="inline-flex items-center gap-2 mt-8 px-6 py-3 border border-champagne text-champagne hover:bg-champagne hover:text-ink-950 transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
          >
            {block.button_text}
          </a>
        )}
      </div>
    </div>
  </Wrap>
);

const ImageBlock = ({ block }) => {
  const src = cmsMediaUrl(block.image);
  if (!src) return null;
  return (
    <Wrap dataTestId={`block-image-${block.id}`}>
      <div className="aspect-[16/9] overflow-hidden bg-ink-900">
        <img
          src={src}
          alt={block.image?.alt_text || block.title || ""}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      {block.title && (
        <p className="mt-4 text-[11px] tracking-[0.3em] uppercase text-zinc-500 font-body">
          {block.title}
        </p>
      )}
    </Wrap>
  );
};

const TextImageBlock = ({ block }) => {
  const src = cmsMediaUrl(block.image);
  return (
    <Wrap dataTestId={`block-textimage-${block.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          {block.subtitle && (
            <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-4">
              {block.subtitle}
            </p>
          )}
          {block.title && (
            <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-[1.1] mb-5">
              {block.title}
            </h2>
          )}
          {block.body && (
            <p className="text-base text-zinc-300 font-body leading-relaxed whitespace-pre-line">
              {block.body}
            </p>
          )}
          {block.button_text && block.button_link && (
            <a
              href={block.button_link}
              className="inline-flex items-center gap-2 mt-7 px-6 py-3 border border-champagne text-champagne hover:bg-champagne hover:text-ink-950 transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
            >
              {block.button_text}
            </a>
          )}
        </div>
        <div className="md:col-span-6 order-1 md:order-2">
          {src ? (
            <div className="aspect-[4/5] overflow-hidden bg-ink-900">
              <img
                src={src}
                alt={block.image?.alt_text || block.title || ""}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="aspect-[4/5] bg-ink-900 border border-white/[0.06]" />
          )}
        </div>
      </div>
    </Wrap>
  );
};

const QuoteBlock = ({ block }) => (
  <section
    data-testid={`block-quote-${block.id}`}
    className="border-y border-white/[0.06] bg-ink-900"
  >
    <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        {block.title && (
          <span className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body">
            {block.title}
          </span>
        )}
      </div>
      <div className="md:col-span-7">
        {block.body && (
          <p className="font-display italic text-2xl md:text-3xl lg:text-4xl text-white leading-[1.25] tracking-tight">
            “{block.body}”
          </p>
        )}
        {block.subtitle && (
          <p className="mt-8 text-sm tracking-[0.2em] uppercase text-zinc-500 font-body">
            {block.subtitle}
          </p>
        )}
      </div>
    </div>
  </section>
);

const CtaBlock = ({ block }) => (
  <Wrap dataTestId={`block-cta-${block.id}`}>
    <div className="bg-ink-900 border border-white/[0.06] p-10 md:p-16 text-center">
      {block.subtitle && (
        <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
          {block.subtitle}
        </p>
      )}
      {block.title && (
        <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-2xl mx-auto leading-[1.1]">
          {block.title}
        </h2>
      )}
      {block.body && (
        <p className="mt-6 text-base text-zinc-300 font-body max-w-xl mx-auto leading-relaxed">
          {block.body}
        </p>
      )}
      {block.button_text && block.button_link && (
        <a
          href={block.button_link}
          className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
        >
          {block.button_text}
        </a>
      )}
    </div>
  </Wrap>
);

const GalleryBlock = ({ block }) => {
  const imgs = block.gallery_images || [];
  if (imgs.length === 0) return null;
  return (
    <Wrap dataTestId={`block-gallery-${block.id}`}>
      {block.title && (
        <h2 className="font-display text-3xl md:text-5xl text-white mb-10 tracking-tight">
          {block.title}
        </h2>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {imgs.map((m) => (
          <div key={m.id} className="aspect-[4/3] overflow-hidden bg-ink-900">
            <img
              src={cmsMediaUrl(m)}
              alt={m.alt_text || ""}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </Wrap>
  );
};

export default ContentBlockRenderer;
